import React, { useEffect, useState } from "react"
import { useTranslation } from "next-i18next"
import StyledModal, { ModalProps } from "../StyledModal"
import close from "../../assets/imgs/home/bg_x.png"
import OtpInput from "react18-input-otp"
import Countdown from "react-countdown"
import { verifyEmailOtp } from "../../services/account"
import { Loading } from "../shared/Loading"
import useGlobalStore from "../../hooks/useGlobalStore"

interface VerifyEmailAddressModalProps extends ModalProps {
    emailAddress: string
    onResendOtp: () => void
    otpCanResendAt: number
    onVerifySuccess: () => void
}

export const VerifyEmailAddressModal = ({
    emailAddress,
    onResendOtp,
    isOpen,
    onRequestClose,
    otpCanResendAt,
    onVerifySuccess
}: VerifyEmailAddressModalProps) => {
    const { t: tCommon } = useTranslation("common")
    const { t: tMyAccount } = useTranslation("my-account")

    const { setPopupMessage } = useGlobalStore()

    const [isLoading, setIsLoading] = useState(false)
    const [countDownSecond, setCountDownSecond] = useState(Math.max(0, otpCanResendAt))
    const [otpValue, setOtpValue] = useState("")

    const renderer = ({ seconds }) => {
        return <span>{seconds}s</span>
    }

    useEffect(() => {
        setCountDownSecond(otpCanResendAt)
    }, [otpCanResendAt])

    const onResend = async () => {
        try {
            setIsLoading(true)

            await onResendOtp()
        } catch (ex) {
            setPopupMessage({ message: ex.response ? ex.response.data.message : ex })
        } finally {
            setIsLoading(false)
        }
    }

    const onVerify = async () => {
        try {
            if (!otpValue || otpValue.length < 6) {
                return
            }

            setIsLoading(true)

            await verifyEmailOtp(otpValue)

            onRequestClose()

            if (typeof onVerifySuccess === "function") {
                onVerifySuccess()
            }
        } catch (ex) {
            setPopupMessage({ message: ex.response ? ex.response.data.message : ex })
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        onVerify()
    }, [otpValue])

    return (
        <StyledModal isOpen={isOpen} onRequestClose={onRequestClose}>
            {isLoading && <Loading />}
            <div className="relative mx-auto w-[300px] rounded-[11px] border border-[#808080] bg-[#F8F8F8] pb-[37px] text-center lg:w-[468px]">
                <div className="absolute right-[13px] top-[13px] text-right">
                    <img src={close.src} onClick={onRequestClose} className="w-[15px] hover:opacity-50 lg:w-[20px]" />
                </div>
                <div className="pt-[37px] text-20 font-bold leading-6 text-neutral-1-500 lg:text-28">
                    {tMyAccount("verifyEmail")}
                </div>
                <div className="mx-auto mt-[21px] max-w-[240px] text-base leading-[19px] text-neutral-1-500 lg:mt-[25px] lg:max-w-[390px]">
                    <div>Enter the OTP you received to: {emailAddress}.</div>
                    <div>
                        Otherwise, Click{" "}
                        <button
                            onClick={onResend}
                            disabled={countDownSecond > 0}
                            className={"text-[#08ACE0] underline disabled:text-neutral-1"}
                        >
                            HERE
                        </button>
                        <span> to resend </span>(
                        {countDownSecond > 0 ? (
                            <Countdown
                                date={Date.now() + countDownSecond * 1000}
                                renderer={renderer}
                                onComplete={() => setCountDownSecond(0)}
                            />
                        ) : (
                            "0s"
                        )}
                        )
                    </div>
                </div>
                <div className={"hidden lg:block"}>
                    <OtpInput
                        value={otpValue}
                        onChange={(otp) => setOtpValue(otp)}
                        numInputs={6}
                        containerStyle={"mt-[21px] w-full flex justify-center items-center"}
                        inputStyle={{
                            width: 46,
                            height: 46,
                            margin: "0 6px",
                            borderRadius: 5,
                            backgroundColor: "#E9EAF0",
                            border: "1px solid #353D49"
                        }}
                        focusStyle={{ outline: "none" }}
                    />
                </div>
                <div className={"block lg:hidden"}>
                    <OtpInput
                        value={otpValue}
                        onChange={(otp) => setOtpValue(otp)}
                        numInputs={6}
                        containerStyle={"mt-[21px] w-full flex justify-center items-center"}
                        inputStyle={{
                            width: 34,
                            height: 34,
                            margin: "0 6px",
                            borderRadius: 5,
                            backgroundColor: "#E9EAF0",
                            border: "1px solid #353D49"
                        }}
                        focusStyle={{ outline: "none" }}
                    />
                </div>
                <div className={"b-flex-center mt-[30px] flex-col lg:flex-row"}>
                    <button
                        onClick={onVerify}
                        className={
                            "h-[31px] w-[143px] rounded-[3px] bg-accent-1 text-14 font-bold text-neutral-1 hover:text-primary lg:mr-3"
                        }
                    >
                        {tCommon("button.confirm")}
                    </button>
                    <button
                        onClick={onRequestClose}
                        className={
                            "mt-5 h-[31px] w-[143px] rounded-[3px] border border-neutral-1 text-14 font-bold text-neutral-1 hover:text-accent-1 lg:mt-0"
                        }
                    >
                        {tCommon("button.cancel")}
                    </button>
                </div>
            </div>
        </StyledModal>
    )
}
